<template>
    <div id="sharedCharts" class="mt-4 w-full">
        <div class="flex flex-wrap items-center">
            <div class="mt-2 xs:pt-3 xs:mt-0 xs:w-full inline-block" v-if="!public">
                <span class="uppercase font-bold mr-1 text-sm">My Shared User ID: <span class="bg-tan-200 rounded py-1 px-2 font-normal">{{ getSharedUserID() }}</span></span>
            </div>
            <div class="mt-2 xs:pt-3 xs:mt-0 xs:mb-2 xs:w-full inline-block" v-else>
                <div class="relative w-full md:w-auto">
                    <input v-focus placeholder="Enter Shared User ID..." class="form-input-inline form-input-sm-inline form-input-inline-w-none -mr-6 pr-6" style="width: auto !important;" v-model="searchQuery">
                    <button class="inline-block search-clear" @click="clearResults"><i class="fas fa-times"></i></button>
                    <button class="inline-block btn btn-sm btn-yellow" @click="searchSharedCharts" :disabled="!searchQuery"><i class="fas fa-search"></i></button>
                </div>
            </div>

            <template v-if="sharedCharts && sharedCharts.length">
                <div class="mt-2 xs:mb-4 xs:mt-0 xs:w-full inline-block ml-auto">
                    <div class="flex items-center">
                        <span class="uppercase font-bold mr-2 text-sm">Section:</span>
                        <div class="inline-block mr-2 xs:mr-0 xs:w-full">
                            <select v-model="currentSection" class="form-control form-control-sm align-bottom bg-white" aria-label="Section">
                                <template v-for="(section, key) in sections">
                                    <option :value="section.value">{{ section.name }}</option>
                                </template>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="mt-2 xs:pt-3 xs:mt-3 xs:hidden xs:w-full inline-block print:hidden">
                    <div class="sharedChartAction w-full inline-flex items-center xs:hidden">
                        <div @click="changeView('single')" class="ml-auto mr-2 text-gray-500" :class="{'text-blue-500': currentView == 'single'}"><i class="fas fa-square"></i></div>
                        <div @click="changeView('double')" class="mr-2 text-gray-500" :class="{'text-blue-500': currentView == 'double'}"><i class="fas fa-columns"></i></div>
                        <div @click="changeView('card')" class="mr-2 text-gray-500" :class="{'text-blue-500': currentView == 'card'}"><i class="fas fa-grip-horizontal"></i></div>
                    </div>
                </div>
            </template>
        </div>

        <div class="-mx-2 flex-wrap flex items-stretch mt-2" v-if="sharedCharts && sharedCharts.length">
            <template v-for="sharedChart in sharedCharts">
                <div v-if="haveSectionItem(sharedChart)" :key="sharedChart.id"
                    class="w-full mb-4 inline-flex"
                    :class="{
                            'sm:w-1/2 md:w-1/3 lg:w-1/3': currentView == 'card',
                            'sm:w-1/2 md:w-1/2 lg:w-1/2': currentView == 'double',
                            '': currentView == 'single'
                    }">
                    <div class="w-full border border-gray-200 rounded mt-3 mr-2 ml-2 p-4 self-stretch">
                        <div class="leading-tight text-center mb-3 text-sm text-gray-500" v-if="showPublicInfo">
                            Shared by User <span @click="filterSharedCharts(sharedChart.user_id)" class="cursor-pointer text-blue-600 bg-tan-200 hover:bg-gray-100 hover:text-black rounded py-1 px-2 font-normal">{{ getSharedUserID(sharedChart.user_id) }}</span>
                        </div>
                        <div class="sharedChartAction flex items-center mb-2 text-md lg:text-lg" v-else>
                            <div class="inline-block mr-1 text-gray-500 text-sm xs:hidden">Share With:</div>
                            <div @click="onClick(sharedChart, 'twitter')" class="text-center mx-2 text-blue-400"><i class="fab fa-x-twitter"></i></div>
                            <div @click="onClick(sharedChart, 'facebook')" class="text-center mr-2 text-blue-600"><i class="fab fa-facebook"></i></div>
                            <div @click="onClick(sharedChart, 'linkedin')" class="text-center mr-2 text-blue-500"><i class="fab fa-linkedin"></i></div>
                            <div @click="onClick(sharedChart, 'copyLink')" class="text-center mr-2 text-gray-500"><i class="fas fa-link"></i></div>
                            <template v-if="!props.public">
                                <div @click="updateSharedChart(sharedChart, 'public')" class="ml-auto mr-2">
                                    <i class="fas" :class="{ 'fa-eye-slash text-gray-500': !sharedChart.public, 'fa-eye text-blue-500': sharedChart.public }"></i>
                                </div>
                                <div v-if="currentSection == 'archived'" @click="restoreSharedChartModal = sharedChart" class="text-blue-500"><i class="fas fa-archive"></i></div>
                                <div v-else @click="archiveSharedChartModal = sharedChart" class="text-red-600"><i class="fas fa-archive"></i></div>
                            </template>
                        </div>
                        <div>
                            <a :href="getSharedChartLink(sharedChart.uuid)" target="_blank">
                                <img class="w-full m-auto text-center" :src="getSharedChartImageLink(sharedChart.uuid)" target="_blank" :alt="'Chart: ' + sharedChart.uuid" />
                            </a>
                        </div>
                        <div class="pt-4">
                            <div class="font-semibold text-blue-600 leading-tight text-center mb-1">
                                <a :href="getSharedChartLink(sharedChart.uuid)" target="_blank">
                                    {{ sharedChart.title }}
                                    <i class="fas fa-long-arrow-alt-right"></i>
                                </a>
                            </div>
                            <p class="text-center text-gray-600 leading-tight text-xs">{{ getSectionName(sharedChart) }} | {{ sharedChart.created_at }}</p>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <div v-else>
            <alert-box>
                <template #body>
                    There are no shared charts.
                </template>
            </alert-box>
        </div>

        <modal @close="archiveSharedChartModal = null" v-if="archiveSharedChartModal" className="max-w-sm w-full">
            <template #header>
                <div class="text-red-500">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Archive Chart
                </div>
            </template>
            <template #body>
                <div class="pt-4 pb-2">
                    <div class="mb-3">
                        <p>Are you sure you want to move the "<strong>{{ archiveSharedChartModal.title }}</strong>"  chart to the archive section?</p>
                        <p class="mt-2 text-gray-600 text-sm">Archived charts will not be deleted and can still appear to the public, but will me moved out of the main chart sections for a simpler viewing experience.</p>
                    </div>
                    <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="archiveSharedChartError">
                        {{ archiveSharedChartError }}
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-red-ghost inline-flex mx-1" @click="archiveSharedChartModal = null">Cancel</div>
                    <div class="btn btn-sm btn-red inline-flex mx-1" @click="updateSharedChart(archiveSharedChartModal, 'disable')">Archive Chart</div>
                </div>
            </template>
        </modal>

        <modal @close="restoreSharedChartModal = null" v-if="restoreSharedChartModal" className="max-w-sm w-full">
            <template #header>
                <div class="text-yellow-500">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Restore Chart
                </div>
            </template>
            <template #body>
                <div class="pt-4 pb-2">
                    <div class="mb-3">
                        <p>Are you sure you want to move the "<strong>{{ restoreSharedChartModal.title }}</strong>" chart back to the main charting section?</p>
                    </div>
                    <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="restoreSharedChartError">
                        {{ restoreSharedChartError }}
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="restoreSharedChartModal = null">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="updateSharedChart(restoreSharedChartModal, 'enable')">Restore Chart</div>
                </div>
            </template>
        </modal>

        <flash-message :secondsToDisplay="3" ref="flash"></flash-message>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';

const props = defineProps({
    initialSharedCharts: {
        type: Array,
        default: () => [],
    },
    initialSections: {
        type: Array,
        default: () => [],
    },
    public: Boolean,
});

const flash = ref(false);
const sharedCharts = ref(props.initialSharedCharts || null);
const sharedSections = ref(props.initialSections || null);
const currentView = ref('card');
const currentSection = ref('');
const archiveSharedChartModal = ref(null);
const archiveSharedChartError = ref('');
const restoreSharedChartModal = ref(null);
const restoreSharedChartError = ref('');
const searchQuery = ref('');
const showPublicInfo = ref(false);
const updated = ref(false);

watch(searchQuery, (val) => {
    if (val == '') {
        clearResults();
    }
});

const sections = computed(() => {
    var computedSections = [];
    if (sharedSections.value) {
        computedSections = sharedSections.value.filter((s) => {
            var haveItems = false;
            sharedCharts.value.forEach(sc => {
                if ((s.value == '' && sc.active == 1) || (sc.section == s.value && sc.active == 1) || (s.value == 'archived' && sc.active == 0)) {
                    haveItems = true;
                }
            });
            return haveItems;
        });

        // Reset current section if no items
        if (!computedSections.find(mSection => mSection.value == currentSection.value)) {
            currentSection.value = computedSections[0]?.value || '';
        }
    }
    return computedSections;
});

const clearResults = () => {
    showPublicInfo.value = true;
    searchQuery.value = '';
    loadSharedCharts();
};

const filterSharedCharts = (userID) => {
    searchQuery.value = getSharedUserID(userID);
    searchSharedCharts();
};

const searchSharedCharts = () => {
    sharedCharts.value = null;
    if (searchQuery.value) {
        showPublicInfo.value = false;
        loadSharedCharts();
    }
};

const getSharedUserID = (userID) => {
    let user_id = userID || window.currentUser.id;
    return (user_id * 112376 + 231176);
};

const haveSectionItem = (sharedChart) => {
    var haveItems = false;
    if ((currentSection.value == '' && sharedChart.active) || (currentSection.value == 'archived' && !sharedChart.active)) {
        haveItems = true;
    } else if (sharedChart.active && sharedChart.section == currentSection.value) {
        haveItems = true;
    }
    return haveItems;
};

const getSectionName = (sharedChart) => {
    if (currentSection.value == 'archived') {
        let metadata = JSON.parse(sharedChart.metadata);
        let section = sharedSections.value.find(mSection => mSection.value == metadata.section);
        if (section) {
            return section.name;
        }
        return 'Archived';
    }
    let section = sharedSections.value.find(mSection => mSection.value == sharedChart.section);
    if (section) {
        return section.name;
    }
    return 'N/A';
};

const getSharedChartLink = (uuid) => {
    return window.route('social-share-view', { 'uuid': uuid });
};

const getSharedChartImageLink = (uuid) => {
    return window.route('social-share-view-image', { 'uuid': uuid }) + '.png';
};

const onClick = (sharedChart, action) => {
    if (action == 'copyLink') {
        // copy to clipboard
        const el = document.createElement('textarea');
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.value = window.route('social-share-view', { 'uuid': sharedChart.uuid });
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        flash.value.display('Link has been copied!');
    } else {
        let metadata = JSON.parse(sharedChart.metadata);
        let twitterSymbol = sharedChart.symbol.replace(/^\^/, "").split(/_/).slice(-2)[0];
        let symbolText = '$' + twitterSymbol + (metadata.symbolRoot ? ' $' + metadata.symbolRoot : '');
        const viaText = "commoditytrends.com";
        let twitterText = 'View my custom ' + sharedChart.title + ' Chart (' + symbolText + ')';
        let shareUrl = window.route('social-share-view', { 'uuid': sharedChart.uuid });
        let actionUrl = {
            twitter: 'https://twitter.com/intent/tweet?url=' + window.encodeURIComponent(shareUrl) + '&text=' + window.encodeURIComponent(twitterText) + '&via=' + window.encodeURIComponent(viaText),
            facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + window.encodeURIComponent(shareUrl) + '&via=' + window.encodeURIComponent(viaText),
            linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=' + window.encodeURIComponent(shareUrl) + '&via=' + window.encodeURIComponent(viaText),
        };
        const params = [
            'height=' + screen.height / 2,
            'width=' + screen.width / 2,
            'fullscreen=no',
            'resizable=yes',
            'scrollbars=yes'
        ].join(', ');
        window.open(
            actionUrl[action],
            'shareWindow-' + action,
            params
        );
    }
};

const updateSharedChart = (sharedChartItem, action) => {
    if (action == 'public') {
        sharedChartItem.public = !sharedChartItem.public;
    }
    if (action == 'disable') {
        sharedChartItem.active = 0;
        archiveSharedChartModal.value = null;
    }
    if (action == 'enable') {
        sharedChartItem.active = 1;
        restoreSharedChartModal.value = null;
    }

    const route = window.route('my-folder.shared-charts.update', sharedChartItem);
    const request = {
        public: sharedChartItem.public,
        active: sharedChartItem.active,
    };

    axios.put(route, request)
        .then((response) => {
            if (response.data.success) {
                flash.value.display('Your shared chart updated!');
                updated.value = true;
            } else {
                flash.value.error('Error updating your shared chart!');
            }
        }).catch((error) => {
            flash.value.error('Error updating your shared chart!');
            console.error(error);
        });
};

const changeView = (action) => {
    if (action == 'single') {
        currentView.value = 'single';
    } else if (action == 'double') {
        currentView.value = 'double';
    } else {
        currentView.value = 'card';
    }
};

const loadSharedCharts = () => {
    const route = window.route('my-folder.shared-charts.index');
    axios.get(route, { params: { 'public': props.public, 'search': searchQuery.value } })
        .then((response) => {
            if (response.data && response.data.sharedCharts && response.data.sharedCharts.length > 0) {
                sharedCharts.value = response.data.sharedCharts;
            } else {
                sharedCharts.value = null;
            }
        }).catch(error => {
        });
};

onMounted(() => {
    if (props.public) {
        showPublicInfo.value = true;
    }
    loadSharedCharts();
});
</script>

<style lang="scss">
#sharedCharts {
    .sharedChartAction {
        i {
            @apply cursor-pointer;
        }
    }
}
</style>
